/**
 * "Read this mail with AI" — pure state machine + wire parsing (Task: extraction-is-async fix).
 *
 * ---- The bug this exists to prevent ----
 *
 * `mail.extract_enquiry_items` was assumed synchronous — call it, get `{items: [...]}` back.
 * Verified live (`rfqdev.etoserp.com`, 2026-08-04) it is not: it QUEUES an AI job and answers
 * immediately with `{job_id}`. The result only ever arrives later, over Socket.io, as an
 * `extract.done` event. Reading `{job_id}` as if it were `{items: [...]}` finds no `items` array
 * and reports "Found 0 line(s)" — indistinguishable, to the purchaser, from a mail that genuinely
 * has none. That is exactly the fact-vs-unknown conflation this codebase rejects everywhere else.
 *
 * This module is the pinned, pure part of the fix: the phases an extraction attempt can be in,
 * the events that move it between them, and the wire parsing for the one event
 * (`extract.done`) that ends the wait. `useMailExtraction.ts` owns the impure half — the actual
 * network call, the socket subscription, the timeout timer — and drives this reducer.
 *
 * ---- The collection is the evidence ----
 *
 * `mail.extract_enquiry_items`'s Postman description (Quoflow_Bosun_API.postman_collection.json,
 * checked 2026-08-04):
 *
 *   Queues an AI extraction job. Returns `job_id` immediately. When complete, publishes
 *   `extract.done` via Socket.io with: `{item_count, items: [{cust_sno, raw_text, item_code,
 *   confidence, qty, uom}]}`
 *
 * That is the ONE payload shape read here. It is a different ruling from `useRealtime.ts`'s —
 * that file reads NO field off ANY realtime payload, because none of the four events' payloads
 * were confirmed and two docs disagreed. `extract.done` is the one exception: this exact shape is
 * documented in the same collection every other confirmed shape in this migration came from, so
 * reading it is evidence-based, not a guess. `useRealtime.ts` keeps its own invalidation
 * payload-free — this is a second, additive consumer of the same event, not a change to that
 * ruling.
 *
 * ---- What is NOT knowable from this payload ----
 *
 * `extract.done` carries no `job_id`, no `communication`, no `enquiry_id` — nothing that ties it
 * back to the request that queued it. There is no way to prove a given `extract.done` belongs to
 * THIS attempt rather than some other extraction in flight (another tab, another purchaser). The
 * best available mitigation, implemented in `useMailExtraction.ts`: only listen while an attempt
 * is actually `pending`, and stop at the first event received — never accept a second, and never
 * listen when idle/succeeded/failed. That narrows the window but cannot close it. Flagged as a
 * backend ask (round 2, A5's neighbour) rather than silently assumed safe.
 *
 * ---- The local fallback (restored) ----
 *
 * `B1` in `docs/bosun-frappe-asks.md`: the job `mail.extract_enquiry_items` queues dies in 38ms on
 * a stale module path — a bug on the backend's side, reported, not ours to fix. Until it ships,
 * the PRIMARY path above still runs first every time (submits, and for the real backend's async
 * shape, waits out the full timeout) — nothing here jumps the queue. Only once it has genuinely
 * failed (`submit-error`/`socket-unavailable`/`timeout`) does `useMailExtraction.ts` attempt the
 * FALLBACK: read attachments client-side and call the older PROPOSED `quoflow.api.
 * extract_enquiry_from_mail` (`mock-server/frappe/extract.ts`, an LLM call that stayed server-side
 * — see that file's header). `result.source` records which path actually answered, so the UI can
 * mark a `'local'` result as the stopgap it is rather than presenting it identically to a genuine
 * server read. `fallback-failed` below is the one new transition this adds: reached only when
 * BOTH the primary call and the fallback attempt failed, from whichever phase the primary attempt
 * left things in (`submitting` — a submit-error or socket-unavailable never reached `pending` — or
 * `pending` — a timeout did).
 */

/** One line the model claims to have found. `item_code` is null unless a code was literally
 * present. Same shape regardless of which path produced it (see the two wire shapes below). */
export interface ExtractedLine {
  item_code: string | null
  description: string
  qty: number
  /**
   * The workbook sheet this line was read from, when the source was a spreadsheet with more than
   * one. Lets the screen offer "one enquiry per sheet" — a deck sheet and an engine sheet are
   * usually two requisitions, not one order split for space.
   */
  sheet?: string
  /** The attachment this line came from, when the mail carried more than one. */
  file?: string
  /**
   * The UOM as VERIFIED against the real `UOM` master (`useMailExtraction.ts`'s `verifyUoms`) —
   * `null` when the model gave none, OR when it gave one the master doesn't recognise. Same
   * treatment `item_code` already gets: `Enquiry Item.uom` is a Frappe Link, so an unverified
   * value would fail the whole document on save (`Could not find Row #1: UOM: NO`) rather than
   * just its own row. Never the model's raw text — see `raw_uom` for that.
   */
  uom: string | null
  /**
   * The customer's/model's ORIGINAL uom text, kept ONLY when `uom` above is `null` because the
   * master didn't recognise it — for a caller that shows a resolution picker beside it so the
   * purchaser knows what they're translating. No screen does that today: `CreateEnquiryBar.tsx`
   * does not carry this field onto `NewEnquiryItem` at all, so an unresolved line reaches
   * `create_enquiry` with `uom: null` and no explanatory text, left for the RFQ builder's own
   * triage. `undefined` when `uom` resolved cleanly (there is nothing to explain) or was absent
   * from the start.
   *
   * NOTE 2026-08-06: this no longer travels to the server as `uom: null` — `uom` turned out to
   * be MANDATORY on the enquiry child row, so `features/mail/createEnquiryLines.ts` substitutes a
   * placeholder and folds THIS value into the line's text instead. It is still the only record of
   * what the customer actually wrote.
   */
  raw_uom?: string
  /** 0–1, the model's own estimate. Not calibrated — present it as a claim, not a fact. */
  confidence: number
  /**
   * Ranked alternatives from `bosun-search`'s `/v1/match`, best first — empty unless that service
   * is configured and answered (`mock-server/frappe/searchMatch.ts`).
   *
   * They exist for the line the matcher could NOT decide. Today such a line offers the purchaser
   * a modal and a blank search box for text the customer already wrote; with these it offers two
   * or three real options to click. Present even when `item_code` was filled in, because the
   * second-best is what a purchaser checks a borderline match against.
   *
   * `score` is the service's calibrated 0–1, or `null` when it sent none — never defaulted to a
   * number, since a fabricated score is exactly what makes a confidence display meaningless.
   */
  candidates?: { item_code: string; item_name: string; score: number | null }[]
}

export interface ExtractionResult {
  enquiry_type: 'Provision' | 'Technical' | null
  customer_hint: string | null
  items: ExtractedLine[]
  notes: string
  /**
   * Real `Customer` docnames the hint resolves to — see `resolveCustomerHint` in
   * `useMailExtraction.ts`. Exactly one means the field can be filled; several means the
   * purchaser is offered the shortlist; none means the hint matched nothing and they search as
   * before.
   */
  customer_matches: string[]
  /**
   * Which path actually produced this result. `'server'` — `mail.extract_enquiry_items` answered
   * (live async job or the mock's synchronous reply). `'local'` — that failed and the client-side
   * attachment reader + `quoflow.api.extract_enquiry_from_mail` fallback ran instead (see the
   * module header, "The local fallback"). The UI must label a `'local'` result as the stopgap it
   * is, never present it as equivalent to a server read: the fallback only has what this browser
   * could download and parse, not whatever the backend's own extraction sees.
   */
  source: 'server' | 'local'
}

/* ============================================================================================
 * The phases
 * ==========================================================================================*/

export type ExtractionFailureReason =
  /**
   * Nothing in the mail could be read — no readable attachment, no table in the body. NOT the same
   * as an order for nothing, which is why it is a failure and not an empty success.
   */
  | 'unreadable'

export type ExtractionPhase =
  | { status: 'idle' }
  /** Reading the mail — attachments downloaded and parsed in the browser. Brief, and local. */
  | { status: 'submitting' }
  | { status: 'succeeded'; result: ExtractionResult }
  | { status: 'failed'; reason: ExtractionFailureReason; message: string }

export const IDLE_PHASE: ExtractionPhase = { status: 'idle' }

export type ExtractionAction =
  | { type: 'submit' }
  /** Items are in hand — read from an attachment or the mail body. */
  | { type: 'succeeded'; result: ExtractionResult }
  /** Nothing could be read. Terminal, and reachable from `submitting` — there is no intermediate
   * state to pass through any more. */
  | { type: 'failed'; reason: ExtractionFailureReason; message: string }
  | { type: 'reset' }

/**
 * The state machine. Every transition that does not apply to the CURRENT phase is a no-op —
 * returning the same `phase` reference unchanged (not a copy), so React's `useReducer` bails out
 * of re-rendering for it. That is what makes a stale event harmless: an `extract.done` that
 * arrives after a timeout already gave up, or a duplicate timeout firing twice, or a
 * `socket-unavailable` check completing after the job already succeeded, all land on a guard that
 * refuses to move the phase — never silently resurrecting or overwriting a settled outcome.
 */
export function extractionReducer(phase: ExtractionPhase, action: ExtractionAction): ExtractionPhase {
  switch (action.type) {
    case 'reset':
      return IDLE_PHASE

    case 'submit':
      // Always allowed — this is how a retry from `failed` (or a fresh read from `succeeded`)
      // starts over.
      return { status: 'submitting' }

    case 'succeeded':
      // Valid from 'submitting' (the mock's synchronous reply, no wait needed), 'pending' (a real
      // `extract.done` ended the wait), or 'falling-back' (the local fallback answered). Anything
      // else — most importantly a SECOND 'succeeded' after the first already landed, or one
      // arriving after 'failed' — is stale.
      if (phase.status !== 'submitting') return phase
      return { status: 'succeeded', result: action.result }

    case 'failed':
      // Reachable from 'submitting', which is the only non-terminal state left. The previous
      // machine routed every failure through 'falling-back' and accepted this action ONLY from
      // there — so once the fallback was deleted, a dispatch from 'submitting' was silently
      // dropped and the phase never left pending. "Running but nothing happens" was exactly that.
      if (phase.status !== 'submitting') return phase
      return { status: 'failed', reason: action.reason, message: action.message }

    default: {
      const unreachable: never = action
      return unreachable
    }
  }
}

/* ============================================================================================
 * `extract.done` wire parsing
 * ==========================================================================================*/

/** One item as the collection documents it inside `extract.done`'s payload — distinct from
 * `ExtractedLine` above: no `description`, has `raw_text` and `cust_sno` instead. */
export interface WireExtractDoneItem {
  cust_sno?: string
  raw_text: string
  item_code: string | null
  confidence: number
  qty: number
  uom: string | null
}

export interface WireExtractDonePayload {
  item_count: number
  items: WireExtractDoneItem[]
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isNullableString(value: unknown): value is string | null | undefined {
  return value === null || value === undefined || typeof value === 'string'
}

function isWireExtractDoneItem(value: unknown): value is WireExtractDoneItem {
  if (typeof value !== 'object' || value === null) return false
  const row = value as Record<string, unknown>
  return (
    typeof row.raw_text === 'string' &&
    isNullableString(row.item_code) &&
    isFiniteNumber(row.confidence) &&
    isFiniteNumber(row.qty) &&
    isNullableString(row.uom)
  )
}

/**
 * Validate a raw realtime payload against the ONE documented `extract.done` shape. Returns `null`
 * for anything that doesn't match — an unrelated event some other consumer might one day publish
 * on this same bus, a malformed message, a payload from a future contract version — rather than
 * throwing or guessing. The caller keeps waiting on `null`; it is not treated as "zero items".
 */
export function parseExtractDonePayload(payload: unknown): WireExtractDonePayload | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (!Array.isArray(row.items) || !row.items.every(isWireExtractDoneItem)) return null
  const items = row.items as WireExtractDoneItem[]
  return { item_count: isFiniteNumber(row.item_count) ? row.item_count : items.length, items }
}

/** `extract.done`'s items → the form's `ExtractedLine`. `raw_text` is the closest analogue to
 * `description` — the model's own words for what it read, not a matched item name. */
export function mapWireExtractDoneItems(items: WireExtractDoneItem[]): ExtractedLine[] {
  return items.map((item) => ({
    item_code: item.item_code ?? null,
    description: item.raw_text,
    qty: item.qty,
    uom: item.uom ?? null,
    confidence: item.confidence,
  }))
}

/* ============================================================================================
 * User-facing messages — pinned here so the reducer and the panel agree on wording.
 * ==========================================================================================*/

export const SOCKET_UNAVAILABLE_MESSAGE =
  "Live updates aren't connected right now, so this reading can never finish here — its result has nowhere to arrive. Reconnect (reload the page) and try again."

export const EXTRACTION_TIMEOUT_MESSAGE =
  'No result came back after waiting. The reading may still finish on the server without us hearing about it — try again.'

/** The specific, actionable message for `local-unavailable` — see `useMailExtraction.ts`'s
 * `classifyFallbackError`. Named so `describeFallbackFailure` in that file and any test asserting
 * on it agree on the exact copy. */
export const LOCAL_FALLBACK_UNAVAILABLE_MESSAGE =
  "the local extraction helper isn't running — start it with `npm run mock`"
